import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import {
  ENQUIRY_CATEGORIES,
  BRANCHES,
  CONTACT_METHODS,
  STATUS_NOTICE,
  NEED_NOTICE,
  WELLBEING_NOTICE,
  CONTACT_CONSENT_LABEL,
  SUPPORT_CIRCLE_CONSENT_LABEL,
  PILLAR_CANONICAL,
  fieldVisible,
  isOrgRequired,
} from "../content/enquiryConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-line bg-sand px-4 py-3 text-sm text-ink placeholder:text-ink-2/50 transition-colors focus:border-action";

const initialContact = { name: "", email: "", phone: "", city: "", preferred_contact: "Email", organisation: "" };

const STEPS = [
  { n: "01", label: "About you" },
  { n: "02", label: "Your reason" },
  { n: "03", label: "A few questions" },
  { n: "04", label: "Review & send" },
];

function Label({ htmlFor, text, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs uppercase tracking-[0.18em] font-semibold text-ink-2 mb-2">
      {text} {required ? <span aria-hidden="true">*</span> : <span className="normal-case font-normal">(optional)</span>}
    </label>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={`error-${id}`} role="alert" className="mt-2 text-sm font-medium text-red-700" data-testid={`error-${id}`}>
      {message}
    </p>
  );
}

function ChipGroup({ field, value, onChange, error }) {
  const selected = Array.isArray(value) ? value : [];
  const isMulti = field.type === "chips";
  const options = field.other ? [...field.options, "Other"] : field.options;

  const toggle = (opt) => {
    if (isMulti) {
      onChange(selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt]);
    } else {
      onChange(selected[0] === opt ? [] : [opt]);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="group" aria-label={field.label}>
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(opt)}
              data-testid={`chip-${field.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                active ? "bg-ink text-sand" : "border border-line bg-sand text-ink-2 hover:text-ink hover:border-ink"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {field.other && selected.includes("Other") && (
        <div className="mt-3">
          <input
            type="text"
            placeholder="Please specify"
            value={field.otherValue || ""}
            onChange={(e) => field.onOtherChange(e.target.value)}
            data-testid={`field-${field.key}-other`}
            className={inputCls}
          />
        </div>
      )}
      {field.hint && <p className="mt-3 text-xs leading-relaxed text-ink-2/80">{field.hint}</p>}
      <FieldError id={field.key} message={error} />
    </div>
  );
}

const valueText = (v) => (Array.isArray(v) ? v.join(", ") : v);

export default function EnquiryForm({ presetCategory }) {
  const [step, setStep] = useState(1);
  const [contact, setContact] = useState(initialContact);
  const [category, setCategory] = useState("");
  const [answers, setAnswers] = useState({});
  const [contactConsent, setContactConsent] = useState(false);
  const [circleConsent, setCircleConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const formTopRef = useRef(null);

  useEffect(() => {
    if (presetCategory && BRANCHES[presetCategory]) {
      setCategory(presetCategory);
      setStep(2);
    }
  }, [presetCategory]);

  const branch = category ? BRANCHES[category] : null;
  const orgRequired = category ? isOrgRequired(category, answers) : false;

  const visibleFields = useMemo(
    () => (branch ? branch.fields.filter((f) => fieldVisible(f, answers)) : []),
    [branch, answers]
  );

  const showWellbeingNote =
    category === "cause" &&
    (answers.pillars || []).some((v) => ["People / Mental Wellbeing", "More than one"].includes(v));

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));
  const updateContact = (e) => setContact((c) => ({ ...c, [e.target.name]: e.target.value }));

  const chooseCategory = (value) => {
    if (value !== category) {
      // Clear only branch-specific answers; keep anything shared (e.g. anything_else)
      setAnswers((a) => (a.anything_else ? { anything_else: a.anything_else } : {}));
    }
    setCategory(value);
  };

  const scrollToTop = () => {
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (contact.name.trim().length < 2) errs.name = "Please tell us your name.";
      if (!contact.email.trim()) errs.email = "Please share your email address.";
      if (contact.city.trim().length < 2) errs.city = "Please tell us your city or location.";
    }
    if (s === 2 && !category) errs.category = "Please choose what brings you to PilCoo.";
    if (s === 3) {
      visibleFields.forEach((f) => {
        if (!f.required) return;
        const v = answers[f.key];
        if (Array.isArray(v)) {
          if (v.length === 0) errs[f.key] = "Please choose at least one option.";
        } else if (!v || String(v).trim().length === 0) {
          errs[f.key] = "This field is required.";
        }
        if (f.other && Array.isArray(v) && v.includes("Other") && !(answers[`${f.key}_other`] || "").trim()) {
          errs[f.key] = "Please tell us a little more about “Other”.";
        }
      });
    }
    if (s === 4) {
      if (orgRequired && contact.organisation.trim().length < 2)
        errs.organisation = "Please tell us your organisation or group name.";
      if (!contactConsent) errs.contact_consent = "Please confirm you're happy for us to contact you.";
      if (branch?.extraConsent && !circleConsent)
        errs.support_circle_consent = "Please confirm the Support Circle consent to continue.";
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep((s) => Math.min(4, s + 1));
    scrollToTop();
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
    scrollToTop();
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = { ...validateStep(1), ...validateStep(2), ...validateStep(3), ...validateStep(4) };
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.name || errs.email || errs.city || errs.organisation) setStep(1);
      else if (errs.category) setStep(2);
      else if (visibleFields.some((f) => errs[f.key])) setStep(3);
      scrollToTop();
      return;
    }
    setStatus("submitting");
    try {
      const responses = visibleFields
        .filter((f) => {
          const v = answers[f.key];
          return Array.isArray(v) ? v.length > 0 : v !== undefined && String(v).trim() !== "";
        })
        .map((f) => {
          let value = answers[f.key];
          if (Array.isArray(value) && f.other && value.includes("Other") && answers[`${f.key}_other`]) {
            value = value.map((v) => (v === "Other" ? `Other: ${answers[`${f.key}_other`]}` : v));
          }
          return { key: f.key, label: f.label, value };
        });

      const pillars = [];
      visibleFields.forEach((f) => {
        if (!f.pillarField) return;
        (answers[f.key] || []).forEach((v) => {
          if (PILLAR_CANONICAL[v] && !pillars.includes(PILLAR_CANONICAL[v])) pillars.push(PILLAR_CANONICAL[v]);
        });
      });

      await axios.post(`${API}/enquiries`, {
        name: contact.name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim() || null,
        city: contact.city.trim(),
        preferred_contact: contact.preferred_contact,
        organisation: contact.organisation.trim() || null,
        enquiry_type: category,
        subject: category === "general" ? answers.subject?.trim() : null,
        message: category === "general" ? answers.message?.trim() : null,
        responses,
        pillars,
        urgency: category === "cause" ? answers.urgency?.[0] || null : null,
        anything_else: category === "general" ? null : answers.anything_else?.trim() || null,
        contact_consent: contactConsent,
        support_circle_consent: branch?.extraConsent ? circleConsent : null,
      });
      setStatus("success");
      toast.success("Your message is on its way. Thank you.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong sending your message. Please try again.";
      setErrors({ submit: msg });
      toast.error("Couldn't send your message. Please try again.");
    }
  };

  const reset = () => {
    setStep(1);
    setContact(initialContact);
    setCategory("");
    setAnswers({});
    setContactConsent(false);
    setCircleConsent(false);
    setErrors({});
    setStatus("idle");
  };

  const reviewRows = useMemo(() => {
    const rows = [
      ["Name", contact.name],
      ["Email", contact.email],
      contact.phone ? ["Phone / WhatsApp", contact.phone] : null,
      ["City / location", contact.city],
      ["Preferred contact", contact.preferred_contact],
      contact.organisation ? ["Organisation / group", contact.organisation] : null,
      ["Enquiry", ENQUIRY_CATEGORIES.find((c) => c.value === category)?.label || ""],
    ];
    visibleFields.forEach((f) => {
      const v = answers[f.key];
      if (Array.isArray(v) ? v.length === 0 : !v || String(v).trim() === "") return;
      let display = valueText(v);
      if (Array.isArray(v) && f.other && v.includes("Other") && answers[`${f.key}_other`]) {
        display = v.map((x) => (x === "Other" ? `Other: ${answers[`${f.key}_other`]}` : x)).join(", ");
      }
      rows.push([f.label, display]);
    });
    if (category !== "general" && answers.anything_else?.trim()) {
      rows.push(["Anything else", answers.anything_else]);
    }
    return rows.filter(Boolean);
  }, [contact, category, answers, visibleFields]);

  if (status === "success") {
    return (
      <div className="border border-line rounded-xl bg-sand p-10 md:p-14 text-center" data-testid="form-success">
        <CheckCircle2 className="h-12 w-12 text-action mx-auto" />
        <h3 className="mt-6 font-serif text-3xl tracking-tight text-ink">Thank you — it&rsquo;s on its way.</h3>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-ink-2 max-w-md mx-auto">
          Your message has reached us. We&rsquo;ve emailed you a copy of your submission, and we&rsquo;ll be in touch soon.
        </p>
        <button
          type="button"
          onClick={reset}
          data-testid="form-send-another"
          className="mt-8 rounded-full border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
        >
          Send another message
        </button>
      </div>
    );
  }

  const navBtn =
    "inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors duration-200";
  const stepHeading = "font-serif text-2xl md:text-3xl tracking-tight text-ink";

  return (
    <form onSubmit={submit} noValidate className="border border-line rounded-xl bg-sand p-7 md:p-10" data-testid="involved-form">
      <div ref={formTopRef} className="scroll-mt-28" />

      {/* Progress indicator */}
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[0.7rem] md:text-xs uppercase tracking-[0.12em] font-semibold" data-testid="form-step-indicator" aria-label="Form progress">
        {STEPS.map((s, i) => {
          const idx = i + 1;
          const isCurrent = step === idx;
          const isDone = step > idx;
          return (
            <li key={s.n} className="flex items-center gap-2">
              {i > 0 && <ArrowRight className="h-3 w-3 text-ink/25" aria-hidden="true" />}
              <span
                aria-current={isCurrent ? "step" : undefined}
                className={isCurrent ? "text-ink" : isDone ? "text-ink/60" : "text-ink/30"}
                data-testid={`step-indicator-${idx}`}
              >
                <span className="font-serif italic normal-case mr-1">{s.n}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-10">
        {/* STEP 1 — About you */}
        {step === 1 && (
          <fieldset data-testid="step-1">
            <legend className={stepHeading}>About you</legend>
            <div className="mt-7 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" text="Full name" required />
                  <input id="name" name="name" type="text" required value={contact.name} onChange={updateContact} placeholder="Full name" data-testid="form-name" className={inputCls} aria-describedby="error-name" />
                  <FieldError id="name" message={errors.name} />
                </div>
                <div>
                  <Label htmlFor="email" text="Email address" required />
                  <input id="email" name="email" type="email" required value={contact.email} onChange={updateContact} placeholder="you@example.com" data-testid="form-email" className={inputCls} aria-describedby="error-email" />
                  <FieldError id="email" message={errors.email} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" text="Phone / WhatsApp number" />
                  <input id="phone" name="phone" type="tel" value={contact.phone} onChange={updateContact} placeholder="+91 …" data-testid="form-phone" className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="city" text="City / location" required />
                  <input id="city" name="city" type="text" required value={contact.city} onChange={updateContact} placeholder="e.g. Bengaluru" data-testid="form-city" className={inputCls} aria-describedby="error-city" />
                  <FieldError id="city" message={errors.city} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="preferred_contact" text="Preferred way for us to contact you" />
                  <select id="preferred_contact" name="preferred_contact" value={contact.preferred_contact} onChange={updateContact} data-testid="form-preferred-contact" className={inputCls}>
                    {CONTACT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="organisation" text="Organisation / group name" required={orgRequired} />
                  <input id="organisation" name="organisation" type="text" value={contact.organisation} onChange={updateContact} placeholder="Shelter, clinic, community group…" data-testid="form-organisation" className={inputCls} aria-describedby="error-organisation" />
                  <FieldError id="organisation" message={errors.organisation} />
                </div>
              </div>
            </div>
          </fieldset>
        )}

        {/* STEP 2 — What brings you to PilCoo? */}
        {step === 2 && (
          <fieldset data-testid="step-2">
            <legend className={stepHeading}>What brings you to PilCoo?</legend>
            <div className="mt-7 grid grid-cols-1 gap-3" role="radiogroup" aria-label="Enquiry category" data-testid="form-enquiry-type">
              {ENQUIRY_CATEGORIES.map((c) => {
                const active = category === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => chooseCategory(c.value)}
                    data-testid={`category-${c.value}`}
                    className={`flex items-center gap-4 rounded-xl border px-5 py-4 text-left text-sm md:text-base font-medium transition-colors duration-200 ${
                      active ? "border-ink bg-ink text-sand" : "border-line bg-sand text-ink hover:border-ink/50"
                    }`}
                  >
                    <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${active ? "border-sand bg-sand" : "border-ink/30"}`} aria-hidden="true" />
                    {c.label}
                  </button>
                );
              })}
            </div>
            <FieldError id="category" message={errors.category} />
            {branch && (
              <Reveal key={category} y={16}>
                <p className="mt-6 text-sm md:text-base leading-relaxed text-ink-2 max-w-2xl border-l-2 border-ink/20 pl-4" data-testid="branch-note">
                  {branch.note}
                </p>
              </Reveal>
            )}
          </fieldset>
        )}

        {/* STEP 3 — A few relevant questions */}
        {step === 3 && branch && (
          <fieldset data-testid="step-3">
            <legend className={stepHeading}>A few relevant questions</legend>
            <div className="mt-8 space-y-8" data-testid="branch-fields">
              {visibleFields.map((f) => (
                <div key={f.key} data-testid={`field-${f.key}`}>
                  {f.type === "chips" || f.type === "choice" ? (
                    <>
                      <p className="block text-xs uppercase tracking-[0.18em] font-semibold text-ink-2 mb-3">
                        {f.label} {f.required ? <span aria-hidden="true">*</span> : <span className="normal-case font-normal">(optional)</span>}
                      </p>
                      <ChipGroup
                        field={{ ...f, otherValue: answers[`${f.key}_other`] || "", onOtherChange: (v) => setAnswer(`${f.key}_other`, v) }}
                        value={answers[f.key] || []}
                        onChange={(v) => setAnswer(f.key, v)}
                        error={errors[f.key]}
                      />
                    </>
                  ) : f.type === "textarea" ? (
                    <>
                      <Label htmlFor={f.key} text={f.label} required={f.required} />
                      <textarea id={f.key} rows={4} value={answers[f.key] || ""} onChange={(e) => setAnswer(f.key, e.target.value)} data-testid={`input-${f.key}`} className={`${inputCls} resize-y`} aria-describedby={`error-${f.key}`} />
                      <FieldError id={f.key} message={errors[f.key]} />
                    </>
                  ) : (
                    <>
                      <Label htmlFor={f.key} text={f.label} required={f.required} />
                      <input id={f.key} type="text" value={answers[f.key] || ""} onChange={(e) => setAnswer(f.key, e.target.value)} placeholder={f.placeholder || ""} data-testid={`input-${f.key}`} className={inputCls} aria-describedby={`error-${f.key}`} />
                      <FieldError id={f.key} message={errors[f.key]} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </fieldset>
        )}

        {/* STEP 4 — Review & send */}
        {step === 4 && branch && (
          <fieldset data-testid="step-4">
            <legend className={stepHeading}>Review & send</legend>

            {/* Review of entered information */}
            <div className="mt-7 rounded-xl border border-line bg-sand-2 p-5 md:p-7" data-testid="review-summary">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-2 mb-4">Your submission</p>
              <dl className="divide-y divide-line">
                {reviewRows.map(([label, value]) => (
                  <div key={label} className="py-2.5 grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4">
                    <dt className="text-xs uppercase tracking-[0.12em] font-semibold text-ink-2">{label}</dt>
                    <dd className="sm:col-span-2 text-sm leading-relaxed text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Anything else */}
            {category !== "general" && (
              <div className="mt-8" data-testid="anything-else-section">
                <Label htmlFor="anything_else" text="Anything else you'd like us to know?" />
                <textarea id="anything_else" rows={4} value={answers.anything_else || ""} onChange={(e) => setAnswer("anything_else", e.target.value)} data-testid="input-anything-else" className={`${inputCls} resize-y`} />
              </div>
            )}

            {/* Notices */}
            <div className="mt-8 space-y-4" data-testid="consent-section">
              <p className="rounded-xl border border-line bg-sand-2 p-5 text-xs md:text-sm leading-relaxed text-ink-2" data-testid="status-notice">
                {STATUS_NOTICE}
              </p>
              {branch.extraNotice === "need" && (
                <p className="rounded-xl border border-line bg-sand-2 p-5 text-xs md:text-sm leading-relaxed text-ink-2" data-testid="need-notice">
                  {NEED_NOTICE}
                </p>
              )}
              {showWellbeingNote && (
                <p className="rounded-xl border border-line bg-sand-2 p-5 text-xs md:text-sm leading-relaxed text-ink-2" data-testid="wellbeing-notice">
                  {WELLBEING_NOTICE}
                </p>
              )}
            </div>

            {/* Consents */}
            <div className="mt-7 space-y-4">
              {branch.extraConsent && (
                <div>
                  <label className="flex items-start gap-3 cursor-pointer text-sm leading-relaxed text-ink" data-testid="support-circle-consent">
                    <input type="checkbox" checked={circleConsent} onChange={(e) => setCircleConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#1C1917]" data-testid="support-circle-consent-checkbox" />
                    {SUPPORT_CIRCLE_CONSENT_LABEL}
                  </label>
                  <FieldError id="support_circle_consent" message={errors.support_circle_consent} />
                </div>
              )}
              <div>
                <label className="flex items-start gap-3 cursor-pointer text-sm leading-relaxed text-ink" data-testid="contact-consent">
                  <input type="checkbox" checked={contactConsent} onChange={(e) => setContactConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#1C1917]" data-testid="contact-consent-checkbox" />
                  {CONTACT_CONSENT_LABEL} <span aria-hidden="true">*</span>
                </label>
                <FieldError id="contact_consent" message={errors.contact_consent} />
              </div>
            </div>

            {errors.submit && (
              <p role="alert" data-testid="form-error" className="mt-6 text-sm font-medium text-red-700">
                {errors.submit}
              </p>
            )}
          </fieldset>
        )}
      </div>

      {/* Navigation */}
      <div className="mt-10 flex items-center justify-between gap-4">
        {step > 1 ? (
          <button type="button" onClick={goBack} data-testid="step-back" className={`${navBtn} border border-line text-ink hover:border-ink`}>
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : (
          <span />
        )}
        {step < 4 ? (
          <button
            type="button"
            onClick={goNext}
            disabled={step >= 2 && !branch}
            data-testid="step-next"
            className={`${navBtn} bg-action text-sand transition-transform duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-40 disabled:pointer-events-none`}
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={status === "submitting"}
            data-testid="form-submit"
            className="rounded-full bg-action text-sand px-8 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-60 disabled:pointer-events-none"
          >
            {status === "submitting" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send message <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
